// ─────────────────────────────────────────────────────────────────────────────
// REALTOR SERVICES AGREEMENT v2.2 — THE DOCUMENT, AS DATA.
//
// 🚨 v2.2 AMENDS §2.1 TO DESCRIBE A THING THE BROKER CAN ACTUALLY DO. v2.1
// obliged the Broker to "notify Doorly, through DoorlyPro, of the property the
// Buyer has selected" — and DoorlyPro has no such mechanism. Checked: there is
// no route for it anywhere in routes/doorlypro.js, and there never was.
//
// ⚠️ THE CLAUSE WAS NOT DELETED, IT WAS CORRECTED. Deleting it would leave
// nothing in §2.1 connecting Home Selection to Doorly learning of it, and §4.2
// conditions payment on the §2.1 services having been performed. The replacement
// states what actually happens: the BUYER requests the offer from their own
// Doorly account, the Broker's job is to send them there, and Doorly tells the
// Broker it landed (which sendPartnerBuyerFoundHomeEmail already does).
//
// ⚠️ NOT FIXED BY BUILDING THE MISSING BUTTON, DELIBERATELY. The Buyer already
// acts; a second way to report the same property invites the two records to
// disagree about which house was selected, and that disagreement would sit
// directly on a payout condition.
//
// 🚨 IT IS DATA BECAUSE IT HAS TO EXIST TWICE AND MUST NOT DIFFER. The partner
// READS it in DoorlyProAgreement.jsx and SIGNS a PDF rendered by
// buildAgreementPdf. Those are two renderers on two sides of a boundary with no
// import path between them, and until this file each carried its own prose —
// which is how the loan-officer rate came to read 0.25 percent in the signed PDF
// and 1.00 percent on the page for three and a half months.
//
// ⚠️ PROSE IN TWO RENDERERS CAN ONLY BE COMPARED BY EYE. Structured clauses can
// be compared by a test: backend/test/agreementTextParity.test.js asserts the two
// copies are clause-for-clause identical.
//
// ⚠️ THE TOKENS ARE SHARED TOO. {{DATE}}, {{BROKER_NAME}}, {{LICENSE_STATE}} and
// {{BROKERAGE}} are interpolated by each renderer from the same fields, so
// neither can invent its own wording around a blank.
//
// ⚠️ IT STOPS BEFORE THE EXECUTION BLOCK. The source document ends with IN
// WITNESS WHEREOF and signature lines; both renderers draw their own signing UI,
// so carrying those here would print two.
//
// ⚠️ THE FILENAME AND EXPORT SAY V2, AND COVER THE WHOLE 2.x LINE. This held v2.0
// for one day. Renaming to V21 would touch both renderers, both mirrors and the
// parity test for no gain — constants/partnerAgreementVersion.js is the
// authoritative version marker, not the filename, and nothing ever signed 2.0.0
// so there is no earlier 2.x text anyone is entitled to have rendered back.
//
// ─────────────────────────────────────────────────────────────────────────────
// 🚨 WHAT v2.1 CHANGED, AND WHY IT MATTERS MORE THAN A POINT RELEASE SOUNDS.
//
// v2.0 §1.4 made a Submission require BOTH a Buyer and a selected property. That
// gated the whole relationship on the buyer having already chosen a house before
// the broker could submit them — backwards for how people actually buy. A buyer
// reasonably wants to know what they are approved for before they shop, which is
// the sequence the product already supports.
//
// v2.1 §1.4: a Submission is JUST A BUYER, and is "the beginning of Broker's
// engagement for that Buyer, not the end of it."
//
// ⚠️ THE SERVICES DID NOT SHRINK — THEY MOVED. New §1.5 names "Home Selection"
// as its own defined term: the identification, showing and advisory work
// resulting in a selected property, which "may occur before or after Submission,
// and ordinarily occurs after the Buyer has been evaluated by Doorly." §2.1 is
// reordered into the real sequence and §2.3 now says the broker's role BEGINS at
// Submission and concludes when Doorly makes an offer — where v2.0 said it
// concluded AT Submission.
//
// 🔑 SO §4.2 NOW HAS TWO TIMING CONDITIONS WHERE IT HAD ONE: a Submission before
// the Buyer contacted Doorly independently, AND Home Selection completed before
// Doorly made an offer. Anything that lists the payment conditions — the training
// deck, the manual — has to carry both or it teaches a condition that no longer
// exists and omits one that does.
//
// ⚠️ THE DEFINITIONS RENUMBERED. Inserting §1.5 pushed everything after it down
// one: Business Day is now §1.9 (was §1.8) and Response Period §1.10 (was §1.9).
// Code comments citing those numbers were updated in the same commit; the
// partner-notices clock cites §1.10 now.
//
// ⚠️ EDITING THIS FILE CHANGES AN EXECUTED INSTRUMENT'S TEXT FOR EVERYONE WHO
// SIGNS AFTERWARDS. A change here is a contract change: bump
// constants/partnerAgreementVersion.js in the same commit, or partners will sign
// different terms under one version number.
export const REALTOR_SERVICES_V2 = [
  {
    "kind": "p",
    "t": "This Realtor Services Agreement (\"Agreement\") is entered into as of {{DATE}}, by and between Go Doorly, LLC, a Delaware limited liability company with its principal place of business at 8708 S Congress Ave, Ste E500-202, Austin, TX 78745 (\"Doorly\"), and {{BROKER_NAME}}, a licensed real estate agent or broker under the laws of the State of {{LICENSE_STATE}}, with principal business address at {{BROKERAGE}} (\"Broker\"). Doorly and Broker may be referred to together as the \"Parties\" and individually as a \"Party.\""
  },
  {
    "kind": "p",
    "t": "RECITALS"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Doorly is a real estate company and a Texas-licensed mortgage company (NMLS #2764749) offering a same-day purchase-and-resale model that enables qualified buyers to become homeowners through its Ability-First Mortgage (\"AFM\") program;"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Broker is a licensed real estate professional who performs buyer-side real estate services — including property identification, showings, and advising a prospective buyer on the selection of a home — and wishes to perform those services in connection with Doorly’s AFM program;"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Doorly does not perform buyer-side property identification or home-selection services, and relies on Broker to perform them;"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Doorly agrees to evaluate buyers under its underwriting guidelines and to perform the real estate acquisition, contract negotiation, and closing activities associated with its model;"
  },
  {
    "kind": "p",
    "t": "NOW, THEREFORE, in consideration of the mutual covenants herein, the Parties agree as follows:"
  },
  {
    "kind": "section",
    "n": "1",
    "t": "DEFINITIONS"
  },
  {
    "kind": "p",
    "t": "For purposes of this Agreement:"
  },
  {
    "kind": "clause",
    "n": "1.1",
    "t": "\"Applicable Law\"",
    "body": [
      "Any federal, state, or local statute, rule, regulation, or requirement governing real estate, seller financing, consumer protection, fair housing, licensing, and privacy."
    ]
  },
  {
    "kind": "clause",
    "n": "1.2",
    "t": "\"Buyer\"",
    "body": [
      "An individual introduced by Broker to Doorly for evaluation under Doorly’s Ability-First Mortgage (AFM) program, for whom Broker performs the Broker Services defined in Section 1.3."
    ]
  },
  {
    "kind": "clause",
    "n": "1.3",
    "t": "\"Broker Services\"",
    "body": [
      "The buyer-side real estate services set out in Section 2.1, which Broker is required to perform. Broker Services include the Submission defined in Section 1.4, property identification, property showings, and advising the Buyer on the selection of a specific home."
    ]
  },
  {
    "kind": "clause",
    "n": "1.4",
    "t": "\"Submission\"",
    "body": [
      "A submission by Broker through DoorlyPro identifying a Buyer. A Submission is the beginning of Broker’s engagement for that Buyer, not the end of it: the Broker Services in Section 2.1 continue after Submission and include advising the Buyer on the selection of a home."
    ]
  },
  {
    "kind": "clause",
    "n": "1.5",
    "t": "\"Home Selection\"",
    "body": [
      "Broker’s performance, for a Buyer, of the property identification, showing, and home-selection advisory services in Section 2.1, resulting in the Buyer’s selection of a specific property that Doorly is asked to acquire. Home Selection may occur before or after Submission, and ordinarily occurs after the Buyer has been evaluated by Doorly."
    ]
  },
  {
    "kind": "clause",
    "n": "1.6",
    "t": "\"Broker Compensation\"",
    "body": [
      "The fee payable to Broker under Section 4 as compensation for the Broker Services actually performed, contingent upon a Successful Closing, and payable only as permitted by Applicable Law."
    ]
  },
  {
    "kind": "clause",
    "n": "1.7",
    "t": "\"Successful Closing\"",
    "body": [
      "A completed real estate purchase by Doorly and resale to the Buyer, funded and closed, with all final documents executed and recorded."
    ]
  },
  {
    "kind": "clause",
    "n": "1.8",
    "t": "\"AFM Program\"",
    "body": [
      "Doorly’s Ability-First Mortgage program — a fully documented underwriting framework that verifies income, assets, credit, and ability-to-repay in accordance with Doorly’s guidelines."
    ]
  },
  {
    "kind": "clause",
    "n": "1.9",
    "t": "\"Business Day\"",
    "body": [
      "Monday through Friday, excluding federal holidays."
    ]
  },
  {
    "kind": "clause",
    "n": "1.10",
    "t": "\"Response Period\"",
    "body": [
      "Two (2) Business Days, measured from Doorly’s delivery of a notice under Section 2.5."
    ]
  },
  {
    "kind": "section",
    "n": "2",
    "t": "BROKER SERVICES AND RESPONSIBILITIES"
  },
  {
    "kind": "clause",
    "n": "2.1",
    "t": "Broker shall perform the following services",
    "body": [
      "Broker is engaged to perform, and shall perform, the following buyer-side real estate services for each Buyer:",
      "Submit the Buyer through DoorlyPro. A Submission may be made before the Buyer has selected a property; Broker need not wait for Home Selection to submit.",
      "Identify properties responsive to the Buyer’s stated criteria and budget.",
      "Arrange and conduct property showings for the Buyer.",
      "Advise the Buyer on the selection of a specific home, applying Broker’s local market knowledge and professional judgment. Home Selection must be completed before Doorly makes an offer on a property for that Buyer.",
      "Direct the Buyer to request the offer on the selected property through the Buyer\u2019s Doorly account. Doorly is notified of the selected property by that request.",
      "Submit accurate information in each Submission, and promptly correct any information Broker learns to be inaccurate.",
      "Remain reasonably available to the Buyer and to Doorly through the Response Period defined in Section 1.10 and the escalation process in Section 2.5.",
      "Represent Doorly’s offering accurately and in accordance with Doorly’s published guidelines.",
      "Maintain an active real estate license in good standing.",
      "Comply with applicable real estate advertising, fair housing, and RESPA requirements.",
      "Direct the Buyer to Doorly for all financing-related questions."
    ]
  },
  {
    "kind": "clause",
    "n": "2.2",
    "t": "Broker shall NOT",
    "body": [
      "Take a mortgage loan application from the Buyer, or complete any part of a Uniform Residential Loan Application on Doorly’s behalf.",
      "Collect, handle, evaluate, or transmit the Buyer’s income, asset, or credit documentation.",
      "Quote, promise, or discuss loan terms, interest rates, pricing, or underwriting outcomes.",
      "Collect any fee, deposit, or other consideration from the Buyer in connection with this Agreement or the AFM Program.",
      "Negotiate on behalf of Doorly in any real estate transaction.",
      "Represent Doorly, or hold Broker out as representing Doorly, in any capacity.",
      "Misrepresent Doorly’s program or guarantee approval.",
      "Engage in any conduct prohibited by state licensing regulations, RESPA, or Applicable Law."
    ]
  },
  {
    "kind": "clause",
    "n": "2.3",
    "t": "Allocation of roles",
    "body": [
      "Doorly acts as the buyer in each transaction. Broker does not represent Doorly and does not participate in writing the Doorly offer. Doorly handles:",
      "offers",
      "negotiations",
      "inspections",
      "closing coordination",
      "Broker’s role is the performance of the Broker Services defined in Section 2.1. That role begins at Submission and continues through Home Selection; it concludes when Doorly makes an offer on the selected property, except for the availability obligation in Section 2.1 and the escalation process in Section 2.5."
    ]
  },
  {
    "kind": "clause",
    "n": "2.4",
    "t": "Service standard",
    "body": [
      "Broker shall perform the Broker Services with the care and diligence customary for a licensed real estate professional representing a buyer in the applicable market."
    ]
  },
  {
    "kind": "clause",
    "n": "2.5",
    "t": "Non-performance and reassignment",
    "body": [
      "The Parties acknowledge that a Buyer’s progress depends on Broker’s continued availability. Accordingly:",
      "If a Buyer reports to Doorly that Broker has not responded to the Buyer, Doorly shall notify Broker in writing at the email address Broker maintains in DoorlyPro, and shall record the date and time of delivery of that notice.",
      "Broker may satisfy that notice within the Response Period by acknowledging it through DoorlyPro or by demonstrating contact with the Buyer.",
      "If Broker does not satisfy the first notice within the Response Period, Doorly shall send a second written notice identifying this Section 2.5 and stating that Doorly may assume the Broker Services for that Buyer.",
      "If Broker does not satisfy the second notice within a further Response Period, Doorly may assume the Broker Services for that Buyer, and Broker Compensation for that Buyer shall not be payable.",
      "Doorly shall not exercise its rights under this Section where its notices were not delivered, or where Broker demonstrates that its non-response was caused by Doorly’s failure to deliver them.",
      "Doorly’s exercise of this Section with respect to one Buyer does not terminate this Agreement or affect any other Buyer."
    ]
  },
  {
    "kind": "section",
    "n": "3",
    "t": "DOORLY RESPONSIBILITIES"
  },
  {
    "kind": "clause",
    "n": "3.1",
    "t": "Doorly shall",
    "body": [
      "Evaluate Buyers under AFM guidelines.",
      "Manage underwriting, processing, and approval decisions.",
      "Act as the purchasing entity and execute all real estate contracts.",
      "Resell the purchased property to the Buyer at closing.",
      "Maintain compliance with all Applicable Law.",
      "Maintain all licensing required for its operations.",
      "Provide high-level status updates through DoorlyPro.",
      "Deliver, and retain a record of, each notice required by Section 2.5.",
      "Notify Broker when the Buyer requests an offer on a selected property."
    ]
  },
  {
    "kind": "clause",
    "n": "3.2",
    "t": "Doorly retains sole discretion over",
    "body": [
      "Buyer approval decisions",
      "Offer amounts",
      "Contract terms",
      "Property acquisition decisions",
      "Closing coordination",
      "Final pricing"
    ]
  },
  {
    "kind": "section",
    "n": "4",
    "t": "COMPENSATION"
  },
  {
    "kind": "clause",
    "n": "4.1",
    "t": "Structure",
    "body": [
      "In consideration of the Broker Services actually performed, Doorly shall pay Broker {{REALTOR_COMP_PCT}} of the home contract purchase price upon a Successful Closing, unless otherwise agreed in writing.",
      "Payment will be made within thirty (30) days of the Successful Closing.",
      "Broker will be compensated only as permissible under Applicable Law, which may include:",
      "A broker-to-broker fee",
      "A fee for services actually performed",
      "A marketing or service fee",
      "Other structures as approved by Doorly’s compliance review"
    ]
  },
  {
    "kind": "clause",
    "n": "4.2",
    "t": "Eligibility conditions",
    "body": [
      "Compensation is payable only if all of the following are satisfied:",
      "Broker performed the Broker Services set out in Section 2.1.",
      "Broker made a Submission for that Buyer before the Buyer contacted Doorly independently.",
      "Broker completed Home Selection for that Buyer before Doorly made an offer on the property.",
      "The transaction resulted in a Successful Closing.",
      "Broker has executed this Agreement and holds the then-current version of it.",
      "Broker has submitted a valid IRS Form W-9.",
      "Broker’s license was active and in good standing at the time of Submission and at closing."
    ]
  },
  {
    "kind": "clause",
    "n": "4.3",
    "t": "Restrictions",
    "body": [
      "Compensation will not be paid if:",
      "The Buyer was already in Doorly’s system.",
      "Doorly had prior documented communication with the Buyer within the preceding twelve (12) months.",
      "The transaction fails to close for any reason.",
      "Broker provided misleading information.",
      "Broker engaged in prohibited conduct, including any RESPA violation or steering.",
      "Doorly assumed the Broker Services for that Buyer under Section 2.5."
    ]
  },
  {
    "kind": "clause",
    "n": "4.4",
    "t": "Held compensation",
    "body": [
      "Where a Successful Closing occurs and Broker has not executed the then-current version of this Agreement, Broker Compensation is earned and held, and shall be released upon Broker’s execution of that version. Compensation held under this Section is not forfeited, and shall be calculated under the terms in force at the time of the Successful Closing."
    ]
  },
  {
    "kind": "section",
    "n": "5",
    "t": "TERM AND TERMINATION"
  },
  {
    "kind": "clause",
    "n": "5.1",
    "t": "Term",
    "body": [
      "This Agreement is effective as of the Effective Date and shall continue for one (1) year, renewing automatically unless terminated by either Party."
    ]
  },
  {
    "kind": "clause",
    "n": "5.2",
    "t": "Termination",
    "body": [
      "Either Party may terminate this Agreement with thirty (30) days’ written notice."
    ]
  },
  {
    "kind": "clause",
    "n": "5.3",
    "t": "Effect of termination",
    "body": [
      "Broker remains eligible for Compensation on any Submission made before termination that results in a Successful Closing, provided Broker performed the Broker Services for that Buyer.",
      "Doorly may assume the Broker Services for any Buyer in progress at the time of termination, without further notice under Section 2.5.",
      "All confidentiality obligations survive termination."
    ]
  },
  {
    "kind": "section",
    "n": "6",
    "t": "CONFIDENTIALITY & NON-DISCLOSURE"
  },
  {
    "kind": "clause",
    "n": "6.1",
    "t": "Confidential Information includes",
    "body": [
      "Buyer data",
      "Underwriting guidelines and pricing",
      "Business strategies",
      "Proprietary technology",
      "Trade secrets",
      "Non-public financial or operational data"
    ]
  },
  {
    "kind": "clause",
    "n": "6.2",
    "t": "Obligations",
    "body": [
      "Both Parties agree to:",
      "Protect confidential material",
      "Not disclose it without written consent",
      "Use it solely for purposes of this Agreement"
    ]
  },
  {
    "kind": "clause",
    "n": "6.3",
    "t": "Exceptions",
    "body": [
      "Confidential Information does not include information that:",
      "Becomes publicly available through no breach",
      "Is lawfully obtained from a third party",
      "Is independently developed without reference to Confidential Information"
    ]
  },
  {
    "kind": "clause",
    "n": "6.4",
    "t": "Required disclosure",
    "body": [
      "If legally compelled, the receiving Party must notify the other Party promptly."
    ]
  },
  {
    "kind": "clause",
    "n": "6.5",
    "t": "Return or destruction",
    "body": [
      "Upon termination, the Parties must return or destroy Confidential Information, except where retention is required by law."
    ]
  },
  {
    "kind": "clause",
    "n": "6.6",
    "t": "Remedies",
    "body": [
      "Both Parties acknowledge:",
      "Breach may cause irreparable harm",
      "Injunctive relief is appropriate",
      "All legal remedies remain available"
    ]
  },
  {
    "kind": "clause",
    "n": "6.7",
    "t": "Survival",
    "body": [
      "Confidentiality obligations survive for five (5) years after termination."
    ]
  },
  {
    "kind": "section",
    "n": "7",
    "t": "COMPLIANCE WITH LAWS"
  },
  {
    "kind": "p",
    "t": "Broker agrees to comply with:"
  },
  {
    "kind": "p",
    "t": "State real estate licensing laws"
  },
  {
    "kind": "p",
    "t": "RESPA"
  },
  {
    "kind": "p",
    "t": "Fair housing laws"
  },
  {
    "kind": "p",
    "t": "Data privacy regulations"
  },
  {
    "kind": "p",
    "t": "Texas real estate advertising rules (if applicable)"
  },
  {
    "kind": "p",
    "t": "Doorly agrees to maintain all licensing required for its operations."
  },
  {
    "kind": "section",
    "n": "8",
    "t": "INDEPENDENT CONTRACTOR RELATIONSHIP"
  },
  {
    "kind": "p",
    "t": "Nothing in this Agreement creates a partnership, joint venture, employment, or agency relationship. Broker cannot bind Doorly in any capacity. Broker performs the Broker Services as an independent contractor, using Broker’s own judgment, methods, and schedule."
  },
  {
    "kind": "section",
    "n": "9",
    "t": "INDEMNIFICATION"
  },
  {
    "kind": "p",
    "t": "Each Party agrees to indemnify and hold harmless the other Party from any claims, liabilities, losses, or damages arising out of its own breach of this Agreement or non-compliance with Applicable Law."
  },
  {
    "kind": "section",
    "n": "10",
    "t": "GOVERNING LAW"
  },
  {
    "kind": "p",
    "t": "This Agreement shall be governed by the laws of the State of Texas, County of Travis, without regard to conflict-of-law principles."
  },
  {
    "kind": "section",
    "n": "11",
    "t": "ENTIRE AGREEMENT"
  },
  {
    "kind": "p",
    "t": "This Agreement contains the entire understanding of the Parties and supersedes all prior written or oral agreements between them on this subject matter, including any prior version of this Agreement executed by Broker."
  },
  {
    "kind": "section",
    "n": "12",
    "t": "AMENDMENTS"
  },
  {
    "kind": "p",
    "t": "No amendment to this Agreement shall be valid unless made in writing and signed by both Parties."
  },
  {
    "kind": "section",
    "n": "13",
    "t": "NOTICES"
  },
  {
    "kind": "p",
    "t": "All notices under this Agreement shall be in writing and delivered to the respective addresses of the Parties as stated above, or via email to legal@godoorly.com. Notices under Section 2.5 may be delivered by email to the address Broker maintains in DoorlyPro, and Broker is responsible for keeping that address current."
  }
];

export default REALTOR_SERVICES_V2;
